// /join redeem-flow logic — the web counterpart to InvitesPage.tsx's
// owner-side minting. Kept as pure functions plus one pure async
// orchestrator (mirrors lib/deleteAccountFlow.ts's split: the page owns
// React state, this owns the decisions).
//
// KEY FINDING that shapes this file (see workers/auth/src/routes/invites.ts):
// invite redemption IS registration — POST /auth/register-with-invite takes
// code + email + display_name + password in one unauthenticated call and
// creates a brand-new acct_kind='invited' account; it never reads the
// caller's session. So there is no "existing account joins" state to
// support — a signed-in visitor isn't redeeming *as* their current account,
// they're about to mint a different one. The one place an existing account
// genuinely matters is `email_taken`: the invite's target email already has
// an account, and the honest next step is signing into *that* one — that's
// the only branch that reuses the app's `from`-location-state return-URL
// pattern (see App.tsx's RequireAuth / LoginPage), via JoinPage linking to
// /login with `state: { from: { pathname: "/join", search } }`.

import { ApiError } from "../api/client";
import type { InviteCheckResponse } from "../api/auth";

// Mirrors workers/auth/src/routes/invites.ts's register-with-invite check
// (`body.password.length < 12`).
export const MIN_PASSWORD_LENGTH = 12;

// ---- Invite validity (GET /auth/invite) ----------------------------------

export type InviteCheckPhase =
  | { status: "checking" }
  | { status: "error"; message: string }
  | { status: "ready"; role: string; emailPinned: boolean };

export function messageForInviteStatus(status: InviteCheckResponse["status"]): string {
  switch (status) {
    case "expired":
      return "This invite has expired. Ask whoever invited you to send a new one.";
    case "redeemed":
      return "This invite has already been used.";
    case "revoked":
      return "This invite was revoked by the person who sent it.";
    case "invalid":
    default:
      return "This invite code isn't valid. Double-check the link, or ask for a new invite.";
  }
}

export function inviteCheckPhaseFromResponse(meta: InviteCheckResponse): InviteCheckPhase {
  if (meta.status === "valid") {
    return { status: "ready", role: meta.role ?? "member", emailPinned: !!meta.email_pinned };
  }
  return { status: "error", message: messageForInviteStatus(meta.status) };
}

export function messageForMissingCode(): string {
  return "This link is missing an invite code.";
}

// ---- Shared error-code mapping (GET check + POST redeem both hit this) ---

type KnownErrorCode =
  | "invite_unavailable"
  | "invite_expired"
  | "invite_email_mismatch"
  | "email_taken"
  | "password_too_short"
  | "invalid_email"
  | "invalid_body"
  | "rate_limited";

const ERROR_MESSAGES: Record<KnownErrorCode, string> = {
  invite_unavailable: "This invite has already been used or was revoked.",
  invite_expired: "This invite has expired. Ask whoever invited you to send a new one.",
  invite_email_mismatch: "This invite was pinned to a different email address.",
  email_taken: "An account with this email already exists.",
  password_too_short: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
  invalid_email: "Enter a valid email address.",
  invalid_body: "Fill in every field.",
  rate_limited: "Too many attempts. Please wait a moment and try again.",
};

function errorCodeFrom(err: unknown): KnownErrorCode | "unknown" {
  if (err instanceof ApiError && err.body && typeof err.body === "object" && "error" in err.body) {
    const code = (err.body as { error?: unknown }).error;
    if (typeof code === "string" && code in ERROR_MESSAGES) return code as KnownErrorCode;
  }
  return "unknown";
}

export function messageForJoinError(err: unknown): string {
  const code = errorCodeFrom(err);
  if (code !== "unknown") return ERROR_MESSAGES[code];
  if (err instanceof ApiError) return err.message;
  return "Join failed. Please try again.";
}

// ---- Form validation -------------------------------------------------

export interface JoinFormInput {
  email: string;
  displayName: string;
  password: string;
}

export function canSubmitJoin(input: JoinFormInput): boolean {
  return (
    input.email.trim().length > 0 &&
    input.displayName.trim().length > 0 &&
    input.password.length >= MIN_PASSWORD_LENGTH
  );
}

// ---- Signed-in-visitor note --------------------------------------------

// Redemption doesn't use the caller's session (see file header), so a
// visitor who's already signed in isn't blocked or bounced through
// /login — they just need to know that submitting mints a *different*
// account and swaps their session out from under them.
export function signedInJoinWarning(currentAccountEmail: string | null): string | null {
  if (!currentAccountEmail) return null;
  return `You're signed in as ${currentAccountEmail}. Joining creates a separate account and will replace your current session.`;
}

// ---- Submit orchestration (POST /auth/register-with-invite) --------------

export type JoinSubmitPhase =
  | { status: "editing" }
  | { status: "submitting" }
  | { status: "failed"; message: string; emailTaken: boolean }
  | { status: "joined" };

// Runs the injected register call and returns the resulting phase — mirrors
// lib/deleteAccountFlow.ts's runDeleteConfirm. No-ops (stays "editing") on
// an incomplete form rather than trusting the caller's disabled-button
// check alone, so a network call can never fire on invalid input.
export async function runJoinSubmit(
  input: JoinFormInput,
  performRegister: () => Promise<void>,
): Promise<JoinSubmitPhase> {
  if (!canSubmitJoin(input)) return { status: "editing" };
  try {
    await performRegister();
    return { status: "joined" };
  } catch (err) {
    return {
      status: "failed",
      message: messageForJoinError(err),
      emailTaken: errorCodeFrom(err) === "email_taken",
    };
  }
}
